import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * Un manual descargable: lo que se ve en su tarjeta y de dónde sale el archivo.
 */
export interface ManualDescargable {
  clave: string;
  titulo: string;
  paraQueSirve: string;
  aQuienSeLeDa: string;
  archivo: string;
  icono: string;
}

/**
 * «Documentación de integración» — consola de CrediTotal.
 *
 * Sirve para que alguien de CrediTotal baje los manuales técnicos y se los mande a un comercio
 * que se va a integrar (a SAP, a Odoo, a quien le lleve el punto de venta), y la colección de
 * Postman para que su desarrollador la pruebe.
 *
 * 🔴 LOS TRES ARCHIVOS LOS PONE JUAN A MANO en `src/assets/manuales/`. Esta pantalla no los
 * genera ni los guarda: sólo los ofrece. Por eso, antes de mostrar el botón de cada tarjeta como
 * disponible, se comprueba con un `HEAD` que el archivo esté de verdad ahí — si Juan todavía no lo
 * subió, la tarjeta se ve igual y el botón lo dice, en vez de ofrecer una descarga que rompe.
 */
@Component({
  selector: 'app-documentacion-integracion',
  templateUrl: './documentacion-integracion.component.html',
})
export class DocumentacionIntegracionComponent implements OnInit {

  readonly manuales: ManualDescargable[] = [
    {
      clave: 'marketplace',
      titulo: 'Manual de integración — Marketplace / canal en línea',
      paraQueSirve: 'Para el comercio que vende desde su propia web o marketplace, donde el '
        + 'cliente paga la inicial él mismo, por débito inmediato.',
      aQuienSeLeDa: 'Se le entrega al equipo técnico del comercio, o a quien le construya esa '
        + 'integración.',
      archivo: 'integracion-marketplace.html',
      icono: 'fa-globe',
    },
    {
      clave: 'caja',
      titulo: 'Manual de integración — Caja / punto de venta',
      paraQueSirve: 'Para el comercio que integra su caja o su ERP, donde es el cajero quien '
        + 'cobra la inicial en el momento de la venta.',
      aQuienSeLeDa: 'Se le entrega a quien integre el punto de venta del comercio — SAP, Odoo o '
        + 'cualquier otro sistema de caja.',
      archivo: 'integracion-caja.html',
      icono: 'fa-cash-register',
    },
    {
      clave: 'postman',
      titulo: 'Colección de Postman',
      paraQueSirve: 'Los llamados de los dos manuales, ya armados y listos para probar, con '
        + 'direcciones de ejemplo.',
      aQuienSeLeDa: 'Se le entrega al desarrollador que va a programar la integración, junto '
        + 'con el manual que le corresponda.',
      archivo: 'creditotal-integracion.postman_collection.json',
      icono: 'fa-terminal',
    },
  ];

  /** true = el archivo está en `assets/manuales/`; false = todavía no; undefined = comprobando. */
  disponible: { [archivo: string]: boolean | undefined } = {};

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    this.manuales.forEach((m) => this.comprobar(m));
  }

  private async comprobar(m: ManualDescargable): Promise<void> {
    this.disponible[m.archivo] = undefined;
    try {
      await firstValueFrom(this.http.head(this.ruta(m), { observe: 'response' }));
      this.disponible[m.archivo] = true;
    } catch {
      this.disponible[m.archivo] = false;
    }
  }

  ruta(m: ManualDescargable): string {
    return `/assets/manuales/${m.archivo}`;
  }

  /** Sólo dispara la descarga si ya se confirmó que el archivo existe. */
  descargar(m: ManualDescargable): void {
    if (!this.disponible[m.archivo]) return;
    const a = document.createElement('a');
    a.href = this.ruta(m);
    a.download = m.archivo;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}
